import type { Context, Effect as EffectType, Layer as LayerType } from 'effect';
import { Effect, Layer, Struct } from 'effect';

/**
 * One capability layer, built from a record of endpoints.
 *
 * A port method is typed `R = never`, so nothing built from raw endpoint
 * functions compiles until the layer captures its runtime context and
 * re-provides it to every call. {@link capabilityLayer} does that capture
 * once, over whatever record of operations it is given, instead of asking
 * every `*Live` module to restate a services union by hand.
 *
 * `operations` reads as the port's own service shape with every method's
 * requirement left in — the same functions a provider builds internally,
 * before the layer strips their requirement away. Each value is either a
 * function returning an `Effect`, matching the port method it answers, or a
 * bare `Effect`, matching a port member with no parameters. Both are
 * accepted in the same record, because the ports in this tier mix the two
 * (`Cart.create` is a bare `Effect`, `Cart.addItem` is a function).
 *
 * Adding an operation is one edit — a new key in the record passed to this
 * function. Forgetting one fails the compiler: `operations` is checked
 * against the tag's own service shape, so a record missing a member cannot
 * be provided to `Layer.effect` and TypeScript rejects the call.
 */

/** `Member`, with its requirement replaced by `R`. */
type Reshape<Member, R> = Member extends (
  ...args: infer Args
) => EffectType.Effect<infer A, infer E, infer _R>
  ? (...args: Args) => EffectType.Effect<A, E, R>
  : Member extends EffectType.Effect<infer A, infer E, infer _R>
    ? EffectType.Effect<A, E, R>
    : never;

/** Every member of `Service`, with its requirement widened to `unknown`. */
type RawOperations<Service> = {
  readonly [K in keyof Service]: Reshape<Service[K], unknown>;
};

/** The requirement one operation carries before it is provided. */
type Requirement<Member> = Member extends (
  ...args: infer _Args
) => EffectType.Effect<infer _A, infer _E, infer R>
  ? R
  : Member extends EffectType.Effect<infer _A, infer _E, infer R>
    ? R
    : never;

/** Every requirement `Ops` carries, as one union — what the layer must capture. */
type Requirements<Ops> = { [K in keyof Ops]: Requirement<Ops[K]> }[keyof Ops];

/**
 * The shape `ProvideLambda` actually runs on: a raw operation with its
 * requirement erased to `unknown`, the widest shape every port member fits.
 */
type AnyOperation =
  | ((...args: ReadonlyArray<never>) => EffectType.Effect<unknown, unknown, unknown>)
  | EffectType.Effect<unknown, unknown, unknown>;

/**
 * The transform `Struct.map` applies to every operation in the record: same
 * shape, requirement replaced by `never`. `Struct.map`'s own typing computes
 * this per key through `"~lambda.out"`, so the result is typed as precisely
 * as the input record — never as the erased {@link AnyOperation}.
 */
interface ProvideLambda extends Struct.Lambda {
  (self: AnyOperation): AnyOperation;
  readonly '~lambda.out': Reshape<this['~lambda.in'], never>;
}

/**
 * The public signature is generic per call site: `Ops` is checked against
 * `RawOperations<Service>` key by key, and `Requirements<Ops>` is read back
 * off the same argument. The implementation below is checked only against
 * its own, concrete signature — TypeScript cannot unfold two nested
 * `Reshape` applications over a still-generic `Ops[K]` to see that they
 * round-trip back to `Service[K]`, so a version of this function typed
 * generic-in-and-out fails to typecheck against itself. The two signatures
 * describe the same runtime behaviour; splitting them is what lets the
 * precise contract stand at every call site without asking the checker to
 * prove an identity it cannot see through a generic parameter.
 */
export function capabilityLayer<
  Self,
  Service extends Record<string, unknown>,
  Ops extends RawOperations<Service>,
>(
  tag: Context.Key<Self, Service>,
  operations: Ops
): LayerType.Layer<Self, never, Requirements<Ops>>;
export function capabilityLayer(
  tag: Context.Key<unknown, Record<string, unknown>>,
  operations: Record<string, AnyOperation>
): LayerType.Layer<unknown, never, unknown> {
  return Layer.effect(
    tag,
    Effect.gen(function* () {
      const services = yield* Effect.context<unknown>();
      const provide = Struct.lambda<ProvideLambda>((operation) =>
        Effect.isEffect(operation)
          ? Effect.provide(operation, services)
          : (...args: ReadonlyArray<never>) => Effect.provide(operation(...args), services)
      );

      return Struct.map(operations, provide);
    })
  );
}
